import React, { useEffect, useRef, useState } from 'react';
import { Shield, CheckCircle, Building, Database, Lock, Award, Globe, UserCheck } from 'lucide-react';

const HealthServices = () => {
  const [activeStep, setActiveStep] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeIn');
            entry.target.classList.remove('opacity-0');
          }
        });
      },
      { threshold: 0.1 }
    );

    const hiddenElements = document.querySelectorAll('.initially-hidden');
    hiddenElements.forEach((el) => observer.observe(el));

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 3000);

    return () => {
      hiddenElements.forEach((el) => observer.unobserve(el));
      clearInterval(interval);
    };
  }, []);

  const processSteps = [
    {
      title: "Assured Quality Checks",
      description: "Exceptional background verification is assured",
      icon: <Shield className="w-8 h-8 text-red-300" />,
      image: "https://images.pexels.com/photos/3825529/pexels-photo-3825529.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
    },
    {
      title: "Due Diligence Checks",
      description: "Helps organizations make compliant informed decisions",
      icon: <CheckCircle className="w-8 h-8 text-red-300" />,
      image: "https://images.pexels.com/photos/3825581/pexels-photo-3825581.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
    },
    {
      title: "Real Time Alerts",
      description: "On employees help HR be proactive and avoid potential damages",
      icon: <UserCheck className="w-8 h-8 text-red-300" />,
      image: "https://images.pexels.com/photos/3825586/pexels-photo-3825586.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
    },
    {
      title: "Seamless Integrations",
      description: "With organization's onboarding/HRMS systems",
      icon: <Database className="w-8 h-8 text-red-300" />,
      image: "https://images.pexels.com/photos/3825538/pexels-photo-3825538.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-950 to-gray-900 text-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/60 to-red-900/90 z-10"></div>
        
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/3825529/pexels-photo-3825529.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
            alt="Healthcare Background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-20 container mx-auto px-4 h-full flex flex-col justify-center">
          <div className="max-w-3xl">
            <div className="inline-block mb-4">
              <div className="bg-white/10 p-3 rounded-full animate-pulse">
                <Shield className="h-8 w-8 text-red-300" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-4 animate-fadeInDown">
              Health Care Industry
            </h1>
            <p className="text-xl text-red-200 mb-8 max-w-2xl animate-fadeInUp">
              Criterion employee background verification ensures a reliable environment for the health care industry.
            </p>
            <div className="flex flex-wrap gap-4 animate-fadeInUp">
              <button className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center"
              onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}>
                <Lock className="w-5 h-5 mr-2" />
                Request a Meeting
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="container mx-auto px-4 py-16">
        {/* Understanding Section */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20 mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">Health Care Industry Challenges</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div>
              <p className="text-lg leading-relaxed mb-6">
                The health care industry faces unique challenges in employee background verification to ensure patient safety and compliance.
              </p>
              <p className="text-lg leading-relaxed">
                Protecting patient data, ensuring workforce quality, and maintaining compliance with regulations are critical priorities.
              </p>
              
              <div className="mt-8 bg-red-900/30 p-6 rounded-lg border border-red-500/30">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Shield className="w-6 h-6 text-red-300 mr-2" />
                  Key Challenges
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Continuous effort to protect patient's confidential information</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Protecting intellectual data</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Ensuring quality of the workforce entrusted with patient lives</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-300 mr-2">✓</span>
                    <span>Need to follow continuous compliance measures</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden h-full shadow-lg">
              <img 
                src="https://images.pexels.com/photos/3825581/pexels-photo-3825581.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                alt="Healthcare Challenges" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-red-900/80 to-transparent flex items-end">
                <p className="p-4 consumerism-white font-medium">Addressing challenges in healthcare verification</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Verification Process Animation */}
        <div className="mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">Criterion's Solution for Health Care Employee Background Verification</h2>
          
          <div className="relative">
            <div className="hidden md:block absolute top-0 left-1/2 w-1 bg-red-500/50 h-full transform -translate-x-1/2 z-0"></div>
            
            {processSteps.map((step, index) => (
              <div 
                key={index} 
                className={`relative z-10 transition-all duration-500 mb-8 ${
                  activeStep === index ? 'scale-100' : 'scale-95 opacity-70'
                }`}
              >
                <div className="flex flex-col md:flex-row items-center">
                  <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:order-2 md:pl-12'}`}>
                    <div className={`bg-white/5 p-6 rounded-lg border border-white/10 transition-all duration-300 ${
                      activeStep === index ? 'bg-white/15 border-red-500/50 shadow-lg' : ''
                    }`}>
                      <div className="flex items-center mb-4 justify-center">
                        <div className={`p-3 rounded-full ${
                          activeStep === index ? 'bg-red-600/30' : 'bg-white/10'
                        }`}>
                          {step.icon}
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-red-300 mb-2">{step.title}</h3>
                      <p className="text-white/80">{step.description}</p>
                    </div>
                  </div>
                  
                  <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:flex items-center justify-center">
                    <div className={`w-10 h-10 rounded-full border-4 z-20 flex items-center justify-center transition-all duration-300 ${
                      activeStep === index 
                        ? 'bg-red-600 border-red-900 scale-125' 
                        : 'bg-red-900/70 border-red-700/50'
                    }`}>
                      <span className="text-white font-bold">{index + 1}</span>
                    </div>
                  </div>
                  
                  <div className={`w-full md:w-1/2 mt-4 md:mt-0 ${index % 2 === 0 ? 'md:order-2 md:pl-12' : 'md:pr-12'}`}>
                    <div className={`h-48 rounded-lg overflow-hidden transition-all duration-300 ${
                      activeStep === index ? 'shadow-2xl ring-2 ring-red-500/50' : ''
                    }`}>
                      <img 
                        src={step.image} 
                        alt={step.title} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Suggested Verifications */}
        <div className="mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">Suggested Verifications</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Education Verification */}
            <div className="bg-gradient-to-br from-red-600/20 to-pink-600/20 rounded-2xl overflow-hidden shadow-xl border border-red-500/30 transform transition-all duration-500 hover:scale-[1.02]">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mr-4">
                    <Globe className="w-8 h-8 text-red-300" />
                  </div>
                  <h3 className="text-2xl font-bold">Education Verification</h3>
                </div>
                <p className="text-white/80">Verifies medical degrees and certifications.</p>
              </div>
              <div className="h-48 relative">
                <img 
                  src="https://images.pexels.com/photos/3825581/pexels-photo-3825581.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                  alt="Education Verification" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/90 to-transparent flex items-end">
                  <p className="p-4 text-white font-medium">Ensuring qualified professionals</p>
                </div>
              </div>
            </div>
            
            {/* Address Verification */}
            <div className="bg-gradient-to-br from-red-900/20 to-red-700/20 rounded-2xl overflow-hidden shadow-xl border border-red-500/30 transform transition-all duration-500 hover:scale-[1.02]">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mr-4">
                    <Building className="w-8 h-8 text-red-300" />
                  </div>
                  <h3 className="text-2xl font-bold">Address Verification</h3>
                </div>
                <p className="text-white/80">Confirms residential details for compliance.</p>
              </div>
              <div className="h-48 relative">
                <img 
                  src="https://images.pexels.com/photos/439391/pexels-photo-439391.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                  alt="Address Verification" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/90 to-transparent flex items-end">
                  <p className="p-4 text-white font-medium">Validating residential information</p>
                </div>
              </div>
            </div>

            {/* Criminal Verification */}
            <div className="bg-gradient-to-br from-red-600/20 to-pink-600/20 rounded-2xl overflow-hidden shadow-xl border border-red-500/30 transform transition-all duration-500 hover:scale-[1.02]">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mr-4">
                    <Shield className="w-8 h-8 text-red-300" />
                  </div>
                  <h3 className="text-2xl font-bold">Criminal Verification</h3>
                </div>
                <p className="text-white/80">Ensures no criminal history for patient safety.</p>
              </div>
              <div className="h-48 relative">
                <img 
                  src="https://images.pexels.com/photos/1632790/pexels-photo-1632790.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                  alt="Criminal Verification" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/90 to-transparent flex items-end">
                  <p className="p-4 text-white font-medium">Protecting patient safety</p>
                </div>
              </div>
            </div>

            {/* Identity Check */}
            <div className="bg-gradient-to-br from-red-900/20 to-red-700/20 rounded-2xl overflow-hidden shadow-xl border border-red-500/30 transform transition-all duration-500 hover:scale-[1.02]">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mr-4">
                    <UserCheck className="w-8 h-8 text-red-300" />
                  </div>
                  <h3 className="text-2xl font-bold">Identity Check</h3>
                </div>
                <p className="text-white/80">Validates identity to prevent fraud in healthcare.</p>
              </div>
              <div className="h-48 relative">
                <img 
                  src="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                  alt="Identity Check" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/90 to-transparent flex items-end">
                  <p className="p-4 text-white font-medium">Preventing identity fraud</p>
                </div>
              </div>
            </div>

            {/* Employment Verification */}
            <div className="bg-gradient-to-br from-red-600/20 to-pink-600/20 rounded-2xl overflow-hidden shadow-xl border border-red-500/30 transform transition-all duration-500 hover:scale-[1.02]">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mr-4">
                    <Award className="w-8 h-8 text-red-300" />
                  </div>
                  <h3 className="text-2xl font-bold">Employment Verification</h3>
                </div>
                <p className="text-white/80">Confirms prior medical employment history.</p>
              </div>
              <div className="h-48 relative">
                <img 
                  src="https://images.pexels.com/photos/3825538/pexels-photo-3825538.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                  alt="Employment Verification" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/90 to-transparent flex items-end">
                  <p className="p-4 text-white font-medium">Validating healthcare experience</p>
                </div>
              </div>
            </div>

            {/* Database Check */}
            <div className="bg-gradient-to-br from-red-900/20 to-red-700/20 rounded-2xl overflow-hidden shadow-xl border border-red-500/30 transform transition-all duration-500 hover:scale-[1.02]">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mr-4">
                    <Database className="w-8 h-8 text-red-300" />
                  </div>
                  <h3 className="text-2xl font-bold">Database Check</h3>
                </div>
                <p className="text-white/80">Cross-references against medical databases.</p>
              </div>
              <div className="h-48 relative">
                <img 
                  src="https://images.pexels.com/photos/1181244/pexels-photo-1181244.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                  alt="Database Check" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/90 to-transparent flex items-end">
                  <p className="p-4 text-white font-medium">Ensuring data accuracy in healthcare</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Best Practices */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20 mb-16 initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-8 text-center">Best Practices for Health Care Verification</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-red-800/30 to-red-600/30 p-6 rounded-xl border border-red-500/20">
              <h3 className="text-2xl font-bold mb-6 text-center">For Organizations</h3>
              
              <div className="space-y-4">
                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Implement thorough background checks for all staff</p>
                </div>
                
                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Partner with healthcare-focused verification providers</p>
                </div>
                
                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Ensure compliance with healthcare regulations</p>
                </div>
                
                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Maintain secure and confidential records</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-red-800/30 to-red-600/30 p-6 rounded-xl border border-red-500/20">
              <h3 className="text-2xl font-bold mb-6 text-center">For Candidates</h3>
              
              <div className="space-y-4">
                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Provide accurate medical qualifications and licenses</p>
                </div>
                
                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Keep medical certifications up to date</p>
                </div>
                
                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Ensure contact details are current</p>
                </div>
                
                <div className="flex items-start p-3 bg-red-900/30 rounded-lg">
                  <div className="bg-red-500/20 rounded-full p-1 mr-3 mt-1 flex-shrink-0">
                    <CheckCircle className="h-4 w-4 text-red-300" />
                  </div>
                  <p>Respond promptly to verification requests</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Statistics Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16 initially-hidden opacity-0">
          {[
            {
              number: "95%",
              text: "of healthcare firms verify backgrounds",
              color: "from-red-600/20 to-red-400/20",
              border: "border-red-500/30"
            },
            {
              number: "10%",
              text: "of candidates misrepresent credentials",
              color: "from-pink-600/20 to-pink-400/20",
              border: "border-pink-500/30"
            },
            {
              number: "3-5",
              text: "days for traditional verification",
              color: "from-orange-600/20 to-orange-400/20",
              border: "border-orange-500/30"
            },
            {
              number: "<2",
              text: "hours for digital verification",
              color: "from-yellow-600/20 to-yellow-400/20",
              border: "border-yellow-500/30"
            }
          ].map((stat, index) => (
            <div 
              key={index}
              className={`bg-gradient-to-br ${stat.color} rounded-xl p-6 shadow-xl ${stat.border} text-center`}
            >
              <div className="text-4xl font-bold text-white mb-2">{stat.number}</div>
              <p className="text-white/80">{stat.text}</p>
            </div>
          ))}
        </div>
        
        {/* Call to Action */}
        <div className="bg-gradient-to-r from-red-600/30 to-pink-600/30 rounded-2xl p-8 shadow-2xl border border-red-500/20 text-center initially-hidden opacity-0">
          <h2 className="text-3xl font-bold mb-4">Take a Tour</h2>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
            To schedule a meeting, click here to ensure secure healthcare verification.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center"
            onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}>
              <Lock className="w-5 h-5 mr-2" />
              Request a Meeting
            </button>
            <button className="bg-transparent border-2 border-red-400 hover:bg-red-400/20 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300">
              Learn More
            </button>
          </div>
        </div>
      </section>

      <style jsx>{`
        .animate-fadeIn {
          animation: fadeIn 1s ease forwards;
        }

        .animate-fadeInDown {
          animation: fadeInDown 1s ease forwards;
        }

        .animate-fadeInUp {
          animation: fadeInUp 1s ease forwards;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default HealthServices;